import { api, ApiError } from "./api";
import {
  ACCEPTED_CONTENT_TYPES,
  MAX_UPLOAD_BYTES,
  type Entry,
  type PresignRequest,
  type PresignResponse,
} from "./types";
import { formatBytes } from "./format";

/**
 * The three-step upload. The photograph never passes through our API:
 *
 *   1. POST /api/uploads/presign        → an Entry in `pending_upload` + a presigned PUT
 *   2. PUT  <uploadUrl>                 → straight to object storage, no credentials
 *   3. POST /api/entries/{id}/uploaded  → flips to `queued`, enqueues the classify job
 */

const ACCEPTED: ReadonlySet<string> = new Set<string>(ACCEPTED_CONTENT_TYPES);

/**
 * Some browsers hand us an empty `type` for HEIC off the camera roll. Fall back
 * to the extension rather than refusing a photo we could have uploaded.
 */
function contentTypeFor(file: File): string {
  if (file.type && ACCEPTED.has(file.type)) return file.type;
  const extension = file.name.slice(file.name.lastIndexOf(".") + 1).toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    case "heic":
      return "image/heic";
    case "heif":
      return "image/heif";
    default:
      return file.type;
  }
}

/**
 * Client-side guards, so an obvious mistake gets a sentence instead of a 4xx.
 * Returns null when the file is fine.
 */
export function validateFile(file: File): string | null {
  const contentType = contentTypeFor(file);

  if (!contentType.startsWith("image/")) {
    return "That isn't a photograph. Foundling can only identify images.";
  }
  if (!ACCEPTED.has(contentType)) {
    return `Foundling can't read ${contentType.replace("image/", "").toUpperCase()} files. Try a JPEG, PNG, WebP or HEIC.`;
  }
  if (file.size === 0) {
    return "That file is empty — the photograph may not have finished saving.";
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `That photograph is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_UPLOAD_BYTES)} — try exporting it a little smaller.`;
  }
  return null;
}

/** The accept attribute for file inputs, derived from the contract's list. */
export const FILE_ACCEPT = ACCEPTED_CONTENT_TYPES.join(",");

/* -------------------------------------------------------------------------- */
/* Step 1                                                                     */
/* -------------------------------------------------------------------------- */

export function presign(file: File): Promise<PresignResponse> {
  const body: PresignRequest = {
    filename: file.name,
    contentType: contentTypeFor(file),
    byteSize: file.size,
  };
  return api.post<PresignResponse>("/uploads/presign", body);
}

/* -------------------------------------------------------------------------- */
/* Step 2                                                                     */
/* -------------------------------------------------------------------------- */

export type PutOptions = {
  url: string;
  headers: Record<string, string>;
  file: File;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
};

/**
 * `fetch` cannot report upload progress — there is no equivalent of
 * `upload.onprogress` — and the developing-photograph interaction is much
 * better with a real bar. So: XHR.
 *
 * `withCredentials` stays false. A presigned URL signs an exact set of headers
 * and rejects anything carrying unexpected auth, so we send the returned
 * headers verbatim and nothing else.
 */
export function putToStorage({
  url,
  headers,
  file,
  onProgress,
  signal,
}: PutOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload cancelled", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.withCredentials = false;

    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }

    const onAbort = () => xhr.abort();
    signal?.addEventListener("abort", onAbort, { once: true });

    const cleanup = () => signal?.removeEventListener("abort", onAbort);

    xhr.upload.onprogress = (event) => {
      if (!onProgress) return;
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(1, event.loaded / event.total));
      }
    };

    xhr.upload.onload = () => onProgress?.(1);

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(
        new ApiError(
          xhr.status,
          xhr.status === 403
            ? "The upload link expired before the photograph finished. Try again."
            : "Object storage refused the upload. Try again in a moment.",
          "storage_put_failed",
        ),
      );
    };

    xhr.onerror = () => {
      cleanup();
      reject(new ApiError(0, "The upload was interrupted. Check your connection and try again."));
    };

    xhr.ontimeout = () => {
      cleanup();
      reject(new ApiError(0, "The upload timed out. Try again on a better connection."));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Upload cancelled", "AbortError"));
    };

    xhr.send(file);
  });
}

/* -------------------------------------------------------------------------- */
/* Step 3                                                                     */
/* -------------------------------------------------------------------------- */

export function markUploaded(entryId: string): Promise<Entry> {
  return api.post<Entry>(`/entries/${entryId}/uploaded`);
}

/* -------------------------------------------------------------------------- */
/* All three                                                                  */
/* -------------------------------------------------------------------------- */

export type UploadPhase = "presigning" | "uploading" | "finalising";

export type RunUploadOptions = {
  file: File;
  onPhase?: (phase: UploadPhase) => void;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
  /** Called as soon as the entry id exists, before the bytes are sent. */
  onEntryId?: (entryId: string) => void;
};

export async function runUpload({
  file,
  onPhase,
  onProgress,
  signal,
  onEntryId,
}: RunUploadOptions): Promise<Entry> {
  onPhase?.("presigning");
  const ticket = await presign(file);
  onEntryId?.(ticket.entryId);

  onPhase?.("uploading");
  await putToStorage({
    url: ticket.uploadUrl,
    headers: ticket.headers,
    file,
    ...(onProgress ? { onProgress } : {}),
    ...(signal ? { signal } : {}),
  });

  onPhase?.("finalising");
  return markUploaded(ticket.entryId);
}
